import { Stack, router, useLocalSearchParams } from 'expo-router';
import { FlagIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';

import { PageHeaderRow } from '@/components/page-header-row';
import { ReportContentSheet } from '@/components/moderation/report-content-sheet';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { useTenant } from '@/components/tenant-provider';
import { Avatar, AvatarFallback, AvatarImage } from '@sixam/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@sixam/ui/card';
import { Text } from '@sixam/ui/text';
import { authedFetch } from '@/lib/authed-fetch';
import { displayMemberName, type ScannedMemberProfile } from '@/lib/member-scan';

export default function MemberProfileScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { activeTenant } = useTenant();
  const [member, setMember] = React.useState<ScannedMemberProfile | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isReportPresented, setIsReportPresented] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    async function load() {
      setIsLoading(true);
      try {
        const response = await authedFetch(`/api/members/${id}`);
        const payload = (await response.json()) as {
          error?: string;
          member?: ScannedMemberProfile;
        };

        if (!response.ok || !payload.member) {
          throw new Error(payload.error ?? 'Member was not found.');
        }

        if (active) {
          setMember(payload.member);
          setError(null);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load member');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      active = false;
    };
  }, [id]);

  const memberName = member ? displayMemberName(member) : 'Member';
  const initials = memberName.slice(0, 2).toUpperCase();

  return (
    <>
      <Stack.Screen options={{ headerShown: process.env.EXPO_OS !== 'web', title: memberName }} />
      <Stack.Screen.BackButton displayMode="minimal">Me</Stack.Screen.BackButton>
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className="flex-1 bg-background"
        contentContainerClassName="gap-4 p-6"
      >
        <PageHeaderRow title={memberName} subtitle={activeTenant?.name} backHref="/app/me" />
        {process.env.EXPO_OS !== 'web' && activeTenant?.name ? (
          <Text variant="muted">{activeTenant.name}</Text>
        ) : null}

        {isLoading ? <ActivityIndicator /> : null}

        {error ? (
          <Text className="text-destructive" selectable>
            {error}
          </Text>
        ) : null}

        {member ? (
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardContent className="gap-4">
              <View className="flex-row items-center gap-4">
                <Avatar alt={`${memberName}'s avatar`} className="size-20">
                  {member.avatarUrl ? <AvatarImage source={{ uri: member.avatarUrl }} /> : null}
                  <AvatarFallback>
                    <Text variant="h3">{initials}</Text>
                  </AvatarFallback>
                </Avatar>
                <View className="min-w-0 flex-1">
                  <Text variant="h2" numberOfLines={2}>
                    {memberName}
                  </Text>
                  {member.email ? (
                    <Text variant="muted" numberOfLines={1}>
                      {member.email}
                    </Text>
                  ) : null}
                </View>
              </View>
              {member.bio ? <Text>{member.bio}</Text> : null}
              {member.phone ? (
                <View className="flex-row justify-between gap-4">
                  <Text variant="muted">Phone</Text>
                  <Text selectable>{member.phone}</Text>
                </View>
              ) : null}

              {/*
                Reporting and blocking live on the profile because that is where
                someone is when they decide they have had enough of a member —
                not buried in settings they would have to go looking for.
              */}
              <Button onPress={() => setIsReportPresented(true)} variant="secondary">
                <Icon as={FlagIcon} className="size-5" />
                <Text>Report or block</Text>
              </Button>
            </CardContent>
          </Card>
        ) : null}
      </ScrollView>

      <ReportContentSheet
        contentLabel={memberName}
        isPresented={isReportPresented}
        onBlocked={() => router.replace('/app/home')}
        onDismiss={() => setIsReportPresented(false)}
        subjectId={id}
        subjectType="profile"
        subjectUserId={id}
      />
    </>
  );
}
